import React, { useEffect, useMemo, useState } from 'react';
import api from '../api/api';
import './operations.css';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const parseDateOnlyUtc = (value) => {
    const normalized = String(value || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return null;
    const [year, month, day] = normalized.split('-').map(Number);
    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const toDisplayDate = (value) => {
    const parsed = parseDateOnlyUtc(value);
    if (!parsed) return '-';
    return parsed.toLocaleDateString('en-US');
};

const addUtcDays = (date, days) => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
};

const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(Number(value) || 0);
};

const normalizeTimeValue = (value) => {
    const normalized = String(value || '').trim();
    return /^([01]\d|2[0-3]):([0-5]\d)$/.test(normalized) ? normalized : '';
};

const getApiErrorMessage = (error, fallback) => {
    const status = Number(error?.response?.status || 0);
    const responseData = error?.response?.data;
    const direct = typeof responseData?.error === 'string'
        ? responseData.error
        : typeof responseData?.error?.message === 'string'
            ? responseData.error.message
            : typeof responseData?.message === 'string'
                ? responseData.message
                : '';

    if (direct) {
        return status > 0 ? `${direct} (HTTP ${status})` : direct;
    }

    if (status === 404) {
        return 'Appointment update endpoint not found (HTTP 404). Restart backend server so latest operations routes are loaded.';
    }

    if (status > 0) {
        return `${fallback} (HTTP ${status})`;
    }

    return error?.message || fallback;
};

const OperationsPage = () => {
    const todayKey = toDateKey(new Date());
    const [anchorDay, setAnchorDay] = useState(todayKey);
    const [scopeKey, setScopeKey] = useState('all');
    const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [appointmentForm, setAppointmentForm] = useState({
        type: 'cleaning',
        propertyId: '',
        appointmentDate: todayKey,
        cleanerId: '',
        cleanerName: '',
        supervisorName: '',
        cost: ''
    });
    const [appointmentFeedback, setAppointmentFeedback] = useState({ type: '', text: '' });
    const [creatingAppointment, setCreatingAppointment] = useState(false);
    const [isCreateAppointmentModalOpen, setIsCreateAppointmentModalOpen] = useState(false);
    const [createAppointmentToast, setCreateAppointmentToast] = useState('');
    const [bookingDetailModal, setBookingDetailModal] = useState({
        isOpen: false,
        property: null,
        dayKey: '',
        mode: '',
        entries: [],
        cleaningAppointments: [],
        supervisorAppointments: []
    });
    const [appointmentDetailModal, setAppointmentDetailModal] = useState({
        isOpen: false,
        property: null,
        dayKey: '',
        entries: []
    });
    const [appointmentDrafts, setAppointmentDrafts] = useState({});
    const [appointmentDetailError, setAppointmentDetailError] = useState('');
    const [savingAppointmentEntryIds, setSavingAppointmentEntryIds] = useState([]);
    const [deletingAppointmentEntryIds, setDeletingAppointmentEntryIds] = useState([]);
    const [bookingTimeDrafts, setBookingTimeDrafts] = useState({});
    const [bookingDetailError, setBookingDetailError] = useState('');
    const [savingBookingIds, setSavingBookingIds] = useState([]);
    const [cleanerByJobId, setCleanerByJobId] = useState({});
    const [calendarData, setCalendarData] = useState({
        startDate: null,
        endDate: null,
        scope: 'all',
        partnerUserId: null,
        partnerOptions: [],
        properties: [],
        cleanerOptions: [],
        cleaningJobs: [],
        bookings: [],
        cleaningAppointments: [],
        supervisorAppointments: []
    });

    const range = useMemo(() => {
        const parsedAnchor = parseDateOnlyUtc(anchorDay) || parseDateOnlyUtc(todayKey) || new Date();

        const startDate = addUtcDays(parsedAnchor, -7);
        const endDate = addUtcDays(parsedAnchor, 30);
        return {
            startDate,
            endDate,
            startDateKey: toDateKey(startDate),
            endDateKey: toDateKey(endDate)
        };
    }, [anchorDay, todayKey]);

    const loadCalendar = async () => {
        setLoading(true);
        setError('');

        try {
            const selectedPartnerId = String(scopeKey || '').startsWith('partner:')
                ? Number(String(scopeKey).split(':')[1])
                : null;
            const scope = selectedPartnerId ? 'partner' : 'all';
            const params = new URLSearchParams({
                startDate: range.startDateKey,
                endDate: range.endDateKey,
                scope
            });
            if (scope === 'partner' && Number.isInteger(selectedPartnerId) && selectedPartnerId > 0) {
                params.set('partnerUserId', String(selectedPartnerId));
            }

            const response = await api.get(`/operations/calendar?${params.toString()}`);
            const payload = response?.data || {};
            setCalendarData({
                startDate: payload.startDate || range.startDateKey,
                endDate: payload.endDate || range.endDateKey,
                scope: payload.scope || 'all',
                partnerUserId: payload.partnerUserId || null,
                partnerOptions: Array.isArray(payload.partnerOptions) ? payload.partnerOptions : [],
                properties: Array.isArray(payload.properties) ? payload.properties : [],
                cleanerOptions: Array.isArray(payload.cleanerOptions) ? payload.cleanerOptions : [],
                cleaningJobs: Array.isArray(payload.cleaningJobs) ? payload.cleaningJobs : [],
                bookings: Array.isArray(payload.bookings) ? payload.bookings : [],
                cleaningAppointments: Array.isArray(payload.cleaningAppointments) ? payload.cleaningAppointments : [],
                supervisorAppointments: Array.isArray(payload.supervisorAppointments) ? payload.supervisorAppointments : []
            });
        } catch (e) {
            console.error('failed to load operations calendar', e);
            setCalendarData({
                startDate: range.startDateKey,
                endDate: range.endDateKey,
                scope: 'all',
                partnerUserId: null,
                partnerOptions: [],
                properties: [],
                cleanerOptions: [],
                cleaningJobs: [],
                bookings: [],
                cleaningAppointments: [],
                supervisorAppointments: []
            });
            setError(e?.response?.data?.error || 'Unable to load operations calendar.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCalendar();
    }, [range.startDateKey, range.endDateKey, scopeKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const dayKeys = useMemo(() => {
        const keys = [];
        let cursor = parseDateOnlyUtc(range.startDateKey);
        const end = parseDateOnlyUtc(range.endDateKey);
        while (cursor && end && cursor < end) {
            keys.push(toDateKey(cursor));
            cursor = addUtcDays(cursor, 1);
        }
        return keys;
    }, [range.endDateKey, range.startDateKey]);

    const filteredProperties = useMemo(() => {
        const allProperties = Array.isArray(calendarData.properties) ? calendarData.properties : [];
        if (selectedPropertyIds.length === 0) return allProperties;
        const selectedSet = new Set(selectedPropertyIds.map((value) => Number(value)).filter((value) => Number.isInteger(value) && value > 0));
        return allProperties.filter((property) => selectedSet.has(Number(property.id)));
    }, [calendarData.properties, selectedPropertyIds]);

    useEffect(() => {
        const propertyIdSet = new Set((calendarData.properties || []).map((property) => Number(property.id)));
        setSelectedPropertyIds((current) => current.filter((id) => propertyIdSet.has(Number(id))));
        setAppointmentForm((current) => {
            const normalizedCurrent = Number(current.propertyId);
            if (normalizedCurrent > 0 && propertyIdSet.has(normalizedCurrent)) return current;
            const firstPropertyId = Number((calendarData.properties || [])[0]?.id || 0);
            return {
                ...current,
                propertyId: firstPropertyId > 0 ? String(firstPropertyId) : ''
            };
        });
    }, [calendarData.properties]);

    useEffect(() => {
        if (!appointmentFeedback.text) return undefined;
        const timeoutId = window.setTimeout(() => {
            setAppointmentFeedback({ type: '', text: '' });
        }, 3000);
        return () => window.clearTimeout(timeoutId);
    }, [appointmentFeedback]);

    useEffect(() => {
        if (!createAppointmentToast) return undefined;
        const timeoutId = window.setTimeout(() => {
            setCreateAppointmentToast('');
        }, 2800);
        return () => window.clearTimeout(timeoutId);
    }, [createAppointmentToast]);

    const dayStatsByProperty = useMemo(() => {
        const map = new Map();
        const rangeStart = parseDateOnlyUtc(range.startDateKey);
        const rangeEnd = parseDateOnlyUtc(range.endDateKey);

        const ensureDayEntry = (propertyId, dayKey) => {
            const normalizedPropertyId = Number(propertyId);
            const propertyMap = map.get(normalizedPropertyId) || new Map();
            const dayEntry = propertyMap.get(dayKey) || {
                occupiedNights: 0,
                arrivals: 0,
                departures: 0,
                cleaningAppointments: [],
                supervisorAppointments: [],
                bookings: [],
                arrivalBookings: [],
                departureBookings: []
            };
            propertyMap.set(dayKey, dayEntry);
            map.set(normalizedPropertyId, propertyMap);
            return dayEntry;
        };

        (calendarData.bookings || []).forEach((booking) => {
            const propertyId = Number(booking.propertyId);
            const bookingStart = parseDateOnlyUtc(booking.startDate);
            const bookingEnd = parseDateOnlyUtc(booking.endDate);
            if (!Number.isInteger(propertyId) || !bookingStart || !bookingEnd || bookingEnd <= bookingStart || !rangeStart || !rangeEnd) {
                return;
            }

            if (bookingStart >= rangeStart && bookingStart < rangeEnd) {
                const arrivalKey = toDateKey(bookingStart);
                const arrivalEntry = ensureDayEntry(propertyId, arrivalKey);
                arrivalEntry.arrivals += 1;
                arrivalEntry.arrivalBookings.push(booking);
            }

            if (bookingEnd > rangeStart && bookingEnd <= rangeEnd) {
                const departureKey = toDateKey(bookingEnd);
                const departureEntry = ensureDayEntry(propertyId, departureKey);
                departureEntry.departures += 1;
                departureEntry.departureBookings.push(booking);
            }

            const visibleStart = bookingStart > rangeStart ? bookingStart : rangeStart;
            const visibleEnd = bookingEnd < rangeEnd ? bookingEnd : rangeEnd;
            let cursor = visibleStart;

            while (cursor < visibleEnd) {
                const dayKey = toDateKey(cursor);
                const entry = ensureDayEntry(propertyId, dayKey);
                entry.occupiedNights += 1;
                entry.bookings.push(booking);
                cursor = addUtcDays(cursor, 1);
            }
        });

        (calendarData.cleaningAppointments || []).forEach((appointment) => {
            const propertyId = Number(appointment.propertyId);
            const appointmentDate = parseDateOnlyUtc(appointment.appointmentDate);
            if (!Number.isInteger(propertyId) || !appointmentDate || !rangeStart || !rangeEnd) return;
            if (appointmentDate < rangeStart || appointmentDate >= rangeEnd) return;

            const dayKey = toDateKey(appointmentDate);
            const entry = ensureDayEntry(propertyId, dayKey);
            entry.cleaningAppointments.push(appointment);
        });

        (calendarData.supervisorAppointments || []).forEach((appointment) => {
            const propertyId = Number(appointment.propertyId);
            const appointmentDate = parseDateOnlyUtc(appointment.appointmentDate);
            if (!Number.isInteger(propertyId) || !appointmentDate || !rangeStart || !rangeEnd) return;
            if (appointmentDate < rangeStart || appointmentDate >= rangeEnd) return;

            const dayKey = toDateKey(appointmentDate);
            const entry = ensureDayEntry(propertyId, dayKey);
            entry.supervisorAppointments.push(appointment);
        });

        return map;
    }, [calendarData.bookings, calendarData.cleaningAppointments, calendarData.supervisorAppointments, range.endDateKey, range.startDateKey]);

    const portfolioStats = useMemo(() => {
        let occupiedNights = 0;
        let arrivals = 0;
        let departures = 0;
        filteredProperties.forEach((property) => {
            const propertyDayMap = dayStatsByProperty.get(Number(property.id));
            if (!propertyDayMap) return;
            propertyDayMap.forEach((entry) => {
                occupiedNights += Number(entry.occupiedNights || 0);
                arrivals += Number(entry.arrivals || 0);
                departures += Number(entry.departures || 0);
            });
        });
        const turnovers = Math.min(arrivals, departures);
        return { occupiedNights, arrivals, departures, turnovers };
    }, [dayStatsByProperty, filteredProperties]);

    const gridDailyTotals = useMemo(() => {
        const totals = new Map();

        dayKeys.forEach((dayKey) => {
            totals.set(dayKey, {
                arrivals: 0,
                departures: 0,
                turnovers: 0
            });
        });

        filteredProperties.forEach((property) => {
            const propertyDayMap = dayStatsByProperty.get(Number(property.id));
            if (!propertyDayMap) return;

            dayKeys.forEach((dayKey) => {
                const entry = propertyDayMap.get(dayKey);
                if (!entry) return;
                const totalsForDay = totals.get(dayKey);
                if (!totalsForDay) return;

                const arrivals = Number(entry.arrivals || 0);
                const departures = Number(entry.departures || 0);
                totalsForDay.arrivals += arrivals;
                totalsForDay.departures += departures;
                totalsForDay.turnovers += Math.min(arrivals, departures);
            });
        });

        return totals;
    }, [dayKeys, dayStatsByProperty, filteredProperties]);

    const departureDayKeysByProperty = useMemo(() => {
        const map = new Map();
        (calendarData.bookings || []).forEach((booking) => {
            const propertyId = Number(booking.propertyId);
            const departureDayKey = String(booking.endDate || '').slice(0, 10);
            if (!Number.isInteger(propertyId) || !parseDateOnlyUtc(departureDayKey)) return;
            const keys = map.get(propertyId) || new Set();
            keys.add(departureDayKey);
            map.set(propertyId, keys);
        });

        const normalized = new Map();
        map.forEach((value, key) => {
            normalized.set(key, Array.from(value).sort((left, right) => left.localeCompare(right)));
        });
        return normalized;
    }, [calendarData.bookings]);

    const supervisorAppointmentDayKeysByProperty = useMemo(() => {
        const map = new Map();
        (calendarData.supervisorAppointments || []).forEach((appointment) => {
            const propertyId = Number(appointment.propertyId);
            const dayKey = String(appointment.appointmentDate || '').slice(0, 10);
            if (!Number.isInteger(propertyId) || !parseDateOnlyUtc(dayKey)) return;
            const keys = map.get(propertyId) || new Set();
            keys.add(dayKey);
            map.set(propertyId, keys);
        });

        const normalized = new Map();
        map.forEach((value, key) => {
            normalized.set(key, Array.from(value).sort((left, right) => left.localeCompare(right)));
        });
        return normalized;
    }, [calendarData.supervisorAppointments]);

    const bookingsByProperty = useMemo(() => {
        const map = new Map();
        (calendarData.bookings || []).forEach((booking) => {
            const propertyId = Number(booking.propertyId);
            if (!Number.isInteger(propertyId) || propertyId <= 0) return;
            const existing = map.get(propertyId) || [];
            existing.push(booking);
            map.set(propertyId, existing);
        });

        map.forEach((entries, key) => {
            entries.sort((left, right) => {
                const leftStart = String(left.startDate || '');
                const rightStart = String(right.startDate || '');
                if (leftStart !== rightStart) return leftStart.localeCompare(rightStart);
                const leftEnd = String(left.endDate || '');
                const rightEnd = String(right.endDate || '');
                return leftEnd.localeCompare(rightEnd);
            });
            map.set(key, entries);
        });

        return map;
    }, [calendarData.bookings]);

    const findLastDepartureDayKey = (propertyId, arrivalDayKey) => {
        const normalizedPropertyId = Number(propertyId);
        const departureDayKeys = departureDayKeysByProperty.get(normalizedPropertyId) || [];
        for (let index = departureDayKeys.length - 1; index >= 0; index -= 1) {
            if (departureDayKeys[index] <= arrivalDayKey) {
                return departureDayKeys[index];
            }
        }
        return null;
    };

    const hasBookingSinceAppointmentBeforeArrival = (propertyId, appointmentDayKey, arrivalDayKey) => {
        if (!appointmentDayKey || !arrivalDayKey || appointmentDayKey >= arrivalDayKey) return false;
        const propertyBookings = bookingsByProperty.get(Number(propertyId)) || [];

        return propertyBookings.some((booking) => {
            const bookingStart = String(booking.startDate || '').slice(0, 10);
            const bookingEnd = String(booking.endDate || '').slice(0, 10);
            if (!bookingStart || !bookingEnd) return false;

            // Any occupied interval between appointment date and arrival date invalidates the appointment.
            const overlapsBetween = bookingStart < arrivalDayKey && bookingEnd > appointmentDayKey;

            // Ignore the arrival booking itself when evaluating "since appointment".
            const isArrivalBooking = bookingStart === arrivalDayKey;
            return overlapsBetween && !isArrivalBooking;
        });
    };

    const isRoleScheduledForArrival = (appointmentsByProperty, propertyId, arrivalDayKey) => {
        const lastDepartureDayKey = findLastDepartureDayKey(propertyId, arrivalDayKey);
        const appointmentDayKeys = appointmentsByProperty.get(Number(propertyId)) || [];

        for (let index = appointmentDayKeys.length - 1; index >= 0; index -= 1) {
            const appointmentDayKey = appointmentDayKeys[index];
            if (appointmentDayKey > arrivalDayKey) continue;
            if (lastDepartureDayKey && appointmentDayKey < lastDepartureDayKey) continue;

            const hasBookingSinceAppointment = hasBookingSinceAppointmentBeforeArrival(propertyId, appointmentDayKey, arrivalDayKey);
            if (!hasBookingSinceAppointment) {
                return true;
            }
        }

        return false;
    };

    const hasAssignedCleanerForBooking = (bookingId) => (calendarData.cleaningJobs || []).some((job) =>
        Number(job.bookingId) === Number(bookingId) && Number(job.assignedCleanerId) > 0
    );

    const getArrivalScheduleStatus = (booking) => {
        const cleanerScheduled = hasAssignedCleanerForBooking(booking.id);
        const supervisorScheduled = isRoleScheduledForArrival(
            supervisorAppointmentDayKeysByProperty,
            Number(booking.propertyId),
            String(booking.startDate || '').slice(0, 10)
        );

        if (!cleanerScheduled) return 'needs_cleaner';
        if (!supervisorScheduled) return 'cleaner_only';
        return 'both';
    };

    const closeBookingDetailModal = () => {
        setBookingDetailModal({ isOpen: false, property: null, dayKey: '', mode: '', entries: [], cleaningAppointments: [], supervisorAppointments: [] });
        setBookingTimeDrafts({});
        setBookingDetailError('');
    };

    const closeAppointmentDetailModal = () => {
        setAppointmentDetailModal({ isOpen: false, property: null, dayKey: '', entries: [] });
        setAppointmentDrafts({});
        setAppointmentDetailError('');
        setDeletingAppointmentEntryIds([]);
    };

    const openBookingDetailModal = (property, dayKey, stats) => {
        const arrivals = Array.isArray(stats?.arrivalBookings) ? stats.arrivalBookings : [];
        const departures = Array.isArray(stats?.departureBookings) ? stats.departureBookings : [];

        const byBookingId = new Map();
        arrivals.forEach((booking) => {
            const bookingId = Number(booking.id);
            if (!Number.isInteger(bookingId) || bookingId <= 0) return;
            const existing = byBookingId.get(bookingId) || { booking, isArrival: false, isDeparture: false };
            existing.isArrival = true;
            existing.booking = booking;
            byBookingId.set(bookingId, existing);
        });

        departures.forEach((booking) => {
            const bookingId = Number(booking.id);
            if (!Number.isInteger(bookingId) || bookingId <= 0) return;
            const existing = byBookingId.get(bookingId) || { booking, isArrival: false, isDeparture: false };
            existing.isDeparture = true;
            existing.booking = booking;
            byBookingId.set(bookingId, existing);
        });

        const entries = Array.from(byBookingId.values())
            .sort((left, right) => {
                const leftDate = String(left.booking.startDate || '');
                const rightDate = String(right.booking.startDate || '');
                if (leftDate !== rightDate) return leftDate.localeCompare(rightDate);
                return Number(left.booking.id || 0) - Number(right.booking.id || 0);
            });

        const hasArrivals = Number(stats?.arrivals || 0) > 0;
        const hasDepartures = Number(stats?.departures || 0) > 0;
        const mode = hasArrivals && hasDepartures
            ? 'turnover'
            : hasArrivals
                ? 'arrival'
                : 'departure';

        const drafts = entries.reduce((acc, entry) => {
            const bookingId = Number(entry.booking.id);
            acc[bookingId] = {
                checkInTime: normalizeTimeValue(entry.booking.checkInTime),
                checkOutTime: normalizeTimeValue(entry.booking.checkOutTime)
            };
            return acc;
        }, {});

        setBookingTimeDrafts(drafts);
        setBookingDetailError('');
        setBookingDetailModal({
            isOpen: true,
            property,
            dayKey,
            mode,
            entries,
            cleaningAppointments: Array.isArray(stats?.cleaningAppointments) ? stats.cleaningAppointments : [],
            supervisorAppointments: Array.isArray(stats?.supervisorAppointments) ? stats.supervisorAppointments : []
        });
    };

    const openAppointmentDetailModal = (property, dayKey, stats) => {
        const cleaningEntries = (Array.isArray(stats?.cleaningAppointments) ? stats.cleaningAppointments : []).map((appointment) => ({
            id: `cleaning-${appointment.id}`,
            appointmentId: Number(appointment.id),
            type: 'cleaning',
            personName: appointment.cleanerName || '',
            cost: appointment.cost,
            appointment
        }));
        const supervisorEntries = (Array.isArray(stats?.supervisorAppointments) ? stats.supervisorAppointments : []).map((appointment) => ({
            id: `supervisor-${appointment.id}`,
            appointmentId: Number(appointment.id),
            type: 'supervisor',
            personName: appointment.supervisorName || '',
            cost: '',
            appointment
        }));

        const entries = [...cleaningEntries, ...supervisorEntries].sort((left, right) => {
            if (left.type !== right.type) return left.type.localeCompare(right.type);
            return String(left.personName || '').localeCompare(String(right.personName || ''));
        });

        const drafts = entries.reduce((acc, entry) => {
            acc[entry.id] = {
                appointmentDate: String(entry.appointment?.appointmentDate || '').slice(0, 10),
                cleanerName: entry.type === 'cleaning' ? String(entry.personName || '') : '',
                supervisorName: entry.type === 'supervisor' ? String(entry.personName || '') : '',
                cost: entry.type === 'cleaning' ? String(entry.cost ?? '') : ''
            };
            return acc;
        }, {});

        setAppointmentDrafts(drafts);
        setAppointmentDetailError('');
        setAppointmentDetailModal({
            isOpen: true,
            property,
            dayKey,
            entries
        });
    };

    const saveAppointment = async (entry) => {
        const entryId = String(entry?.id || '');
        const appointmentId = Number(entry?.appointmentId);
        if (!entryId || !Number.isInteger(appointmentId) || appointmentId <= 0) return;

        const draft = appointmentDrafts[entryId] || {};
        setAppointmentDetailError('');
        setSavingAppointmentEntryIds((current) => [...new Set([...current, entryId])]);

        try {
            if (entry.type === 'cleaning') {
                const response = await api.put(`/operations/cleaning-appointments/${appointmentId}`, {
                    appointmentDate: String(draft.appointmentDate || '').slice(0, 10),
                    cleanerId: draft.cleanerId ? Number(draft.cleanerId) : null,
                    cleanerName: String(draft.cleanerName || '').trim(),
                    cost: Number(draft.cost) || 0
                });
                const updated = response?.data || {};
                const nextAppointmentDate = String(updated.appointmentDate || draft.appointmentDate || '').slice(0, 10);
                const nextCleanerId = updated.cleanerId ? String(updated.cleanerId) : String(draft.cleanerId || '');
                const nextCleanerName = String(updated.cleanerName || draft.cleanerName || '').trim();
                const nextCost = Number(updated.cost ?? draft.cost ?? 0);

                setAppointmentDrafts((current) => ({
                    ...current,
                    [entryId]: {
                        ...(current[entryId] || {}),
                        appointmentDate: nextAppointmentDate,
                        cleanerId: nextCleanerId,
                        cleanerName: nextCleanerName,
                        cost: String(nextCost)
                    }
                }));

                setAppointmentDetailModal((current) => ({
                    ...current,
                    entries: (current.entries || []).map((item) => {
                        if (String(item.id) !== entryId) return item;
                        return {
                            ...item,
                            personName: nextCleanerName,
                            cost: nextCost,
                            appointment: {
                                ...item.appointment,
                                appointmentDate: nextAppointmentDate,
                                cleanerId: nextCleanerId ? Number(nextCleanerId) : null,
                                cleanerName: nextCleanerName,
                                cost: nextCost
                            }
                        };
                    })
                }));

                setCalendarData((current) => ({
                    ...current,
                    cleaningAppointments: (current.cleaningAppointments || []).map((item) => {
                        if (Number(item.id) !== appointmentId) return item;
                        return {
                            ...item,
                            appointmentDate: nextAppointmentDate,
                            cleanerId: nextCleanerId ? Number(nextCleanerId) : null,
                            cleanerName: nextCleanerName,
                            cost: nextCost
                        };
                    })
                }));
            } else {
                const response = await api.put(`/operations/supervisor-appointments/${appointmentId}`, {
                    appointmentDate: String(draft.appointmentDate || '').slice(0, 10),
                    supervisorName: String(draft.supervisorName || '').trim()
                });
                const updated = response?.data || {};
                const nextAppointmentDate = String(updated.appointmentDate || draft.appointmentDate || '').slice(0, 10);
                const nextSupervisorName = String(updated.supervisorName || draft.supervisorName || '').trim();

                setAppointmentDrafts((current) => ({
                    ...current,
                    [entryId]: {
                        ...(current[entryId] || {}),
                        appointmentDate: nextAppointmentDate,
                        supervisorName: nextSupervisorName
                    }
                }));

                setAppointmentDetailModal((current) => ({
                    ...current,
                    entries: (current.entries || []).map((item) => {
                        if (String(item.id) !== entryId) return item;
                        return {
                            ...item,
                            personName: nextSupervisorName,
                            appointment: {
                                ...item.appointment,
                                appointmentDate: nextAppointmentDate,
                                supervisorName: nextSupervisorName
                            }
                        };
                    })
                }));

                setCalendarData((current) => ({
                    ...current,
                    supervisorAppointments: (current.supervisorAppointments || []).map((item) => {
                        if (Number(item.id) !== appointmentId) return item;
                        return {
                            ...item,
                            appointmentDate: nextAppointmentDate,
                            supervisorName: nextSupervisorName
                        };
                    })
                }));
            }
        } catch (saveError) {
            setAppointmentDetailError(getApiErrorMessage(saveError, 'Unable to save appointment.'));
        } finally {
            setSavingAppointmentEntryIds((current) => current.filter((id) => id !== entryId));
        }
    };

    const deleteAppointment = async (entry) => {
        const entryId = String(entry?.id || '');
        const appointmentId = Number(entry?.appointmentId);
        if (!entryId || !Number.isInteger(appointmentId) || appointmentId <= 0) return;

        const confirmed = window.confirm('Delete this appointment? This cannot be undone.');
        if (!confirmed) return;

        setAppointmentDetailError('');
        setDeletingAppointmentEntryIds((current) => [...new Set([...current, entryId])]);

        try {
            if (entry.type === 'cleaning') {
                await api.delete(`/operations/cleaning-appointments/${appointmentId}`);
                setCalendarData((current) => ({
                    ...current,
                    cleaningAppointments: (current.cleaningAppointments || []).filter((item) => Number(item.id) !== appointmentId)
                }));
            } else {
                await api.delete(`/operations/supervisor-appointments/${appointmentId}`);
                setCalendarData((current) => ({
                    ...current,
                    supervisorAppointments: (current.supervisorAppointments || []).filter((item) => Number(item.id) !== appointmentId)
                }));
            }

            setAppointmentDrafts((current) => {
                const next = { ...current };
                delete next[entryId];
                return next;
            });

            setAppointmentDetailModal((current) => {
                const remainingEntries = (current.entries || []).filter((item) => String(item.id) !== entryId);
                return {
                    ...current,
                    entries: remainingEntries,
                    isOpen: remainingEntries.length > 0
                };
            });

            setBookingDetailModal((current) => ({
                ...current,
                cleaningAppointments: entry.type === 'cleaning'
                    ? (current.cleaningAppointments || []).filter((item) => Number(item.id) !== appointmentId)
                    : current.cleaningAppointments,
                supervisorAppointments: entry.type === 'supervisor'
                    ? (current.supervisorAppointments || []).filter((item) => Number(item.id) !== appointmentId)
                    : current.supervisorAppointments
            }));
        } catch (deleteError) {
            setAppointmentDetailError(getApiErrorMessage(deleteError, 'Unable to delete appointment.'));
        } finally {
            setDeletingAppointmentEntryIds((current) => current.filter((id) => id !== entryId));
        }
    };

    const saveBookingTimes = async (entry) => {
        const bookingId = Number(entry?.booking?.id);
        if (!Number.isInteger(bookingId) || bookingId <= 0) return;

        const draft = bookingTimeDrafts[bookingId] || { checkInTime: '', checkOutTime: '' };
        setBookingDetailError('');
        setSavingBookingIds((current) => [...new Set([...current, bookingId])]);

        try {
            const response = await api.put(`/booking/${bookingId}/times`, {
                checkInTime: normalizeTimeValue(draft.checkInTime),
                checkOutTime: normalizeTimeValue(draft.checkOutTime)
            });

            const updated = response?.data || {};
            const nextCheckInTime = normalizeTimeValue(updated.checkInTime || draft.checkInTime);
            const nextCheckOutTime = normalizeTimeValue(updated.checkOutTime || draft.checkOutTime);

            setBookingTimeDrafts((current) => ({
                ...current,
                [bookingId]: {
                    checkInTime: nextCheckInTime,
                    checkOutTime: nextCheckOutTime
                }
            }));

            setBookingDetailModal((current) => ({
                ...current,
                entries: (current.entries || []).map((item) => {
                    if (Number(item?.booking?.id) !== bookingId) return item;
                    return {
                        ...item,
                        booking: {
                            ...item.booking,
                            checkInTime: nextCheckInTime,
                            checkOutTime: nextCheckOutTime
                        }
                    };
                })
            }));

            setCalendarData((current) => ({
                ...current,
                bookings: (current.bookings || []).map((booking) => {
                    if (Number(booking?.id) !== bookingId) return booking;
                    return {
                        ...booking,
                        checkInTime: nextCheckInTime,
                        checkOutTime: nextCheckOutTime
                    };
                })
            }));
        } catch (saveError) {
            setBookingDetailError(saveError?.response?.data?.error || 'Unable to save booking times.');
        } finally {
            setSavingBookingIds((current) => current.filter((id) => id !== bookingId));
        }
    };

    const assignBookingCleaner = async (job) => {
        const jobId = Number(job?.id);
        const cleanerId = Number(cleanerByJobId[jobId]);
        if (!Number.isInteger(jobId) || jobId <= 0 || !Number.isInteger(cleanerId) || cleanerId <= 0) return;
        setBookingDetailError('');
        setSavingBookingIds((current) => [...new Set([...current, jobId])]);
        try {
            await api.put(`/cleaner-workforce/jobs/${jobId}/assign`, { cleanerId });
            await loadCalendar();
        } catch (assignError) {
            setBookingDetailError(assignError?.response?.data?.error || 'Unable to offer this cleaning to the selected cleaner.');
        } finally {
            setSavingBookingIds((current) => current.filter((id) => id !== jobId));
        }
    };

    const createAppointment = async (event) => {
        event.preventDefault();
        setAppointmentFeedback({ type: '', text: '' });

        const propertyId = Number(appointmentForm.propertyId);
        if (!Number.isInteger(propertyId) || propertyId <= 0) {
            setAppointmentFeedback({ type: 'error', text: 'Choose a property.' });
            return;
        }

        try {
            setCreatingAppointment(true);

            if (appointmentForm.type === 'cleaning') {
                const selectedCleaner = calendarData.cleanerOptions.find((cleaner) => Number(cleaner.cleanerId) === Number(appointmentForm.cleanerId));
                await api.post('/operations/cleaning-appointments', {
                    propertyId,
                    appointmentDate: appointmentForm.appointmentDate,
                    cleanerId: appointmentForm.cleanerId ? Number(appointmentForm.cleanerId) : null,
                    cleanerName: selectedCleaner?.display || appointmentForm.cleanerName,
                    cost: Number(appointmentForm.cost) || 0
                });
            } else {
                await api.post('/operations/supervisor-appointments', {
                    propertyId,
                    appointmentDate: appointmentForm.appointmentDate,
                    supervisorName: appointmentForm.supervisorName
                });
            }

            setAppointmentForm((current) => ({
                ...current,
                appointmentDate: current.appointmentDate || todayKey,
                cleanerId: '',
                cleanerName: '',
                supervisorName: '',
                cost: ''
            }));
            setAppointmentFeedback({
                type: 'success',
                text: appointmentForm.type === 'cleaning' ? 'Cleaning appointment created.' : 'Supervisor appointment created.'
            });
            await loadCalendar();
        } catch (createError) {
            setAppointmentFeedback({ type: 'error', text: createError?.response?.data?.error || 'Unable to create appointment.' });
        } finally {
            setCreatingAppointment(false);
        }
    };

    const closeCreateAppointmentModal = () => {
        setIsCreateAppointmentModalOpen(false);
        setAppointmentFeedback({ type: '', text: '' });
    };

    const openCreateAppointmentModalForCell = (property, dayKey) => {
        setAppointmentFeedback({ type: '', text: '' });
        setAppointmentForm((current) => ({
            ...current,
            propertyId: String(property?.id ?? ''),
            appointmentDate: dayKey || todayKey
        }));
        setIsCreateAppointmentModalOpen(true);
    };

    return (
        <div className="operations-page operations-calendar-page">
            <section className="property-pnl-card operations-toolbar-card">
                <div className="operations-toolbar-grid">
                    <div className="operations-toolbar-fields">
                        <label className="operations-filter-field">
                            <span>Portfolio Filter</span>
                            <select value={scopeKey} onChange={(event) => setScopeKey(event.target.value)}>
                                <option value="all">All Owned Properties</option>
                                {(calendarData.partnerOptions || []).map((partner) => (
                                    <option key={partner.id} value={`partner:${partner.id}`}>
                                        With {partner.display || partner.email}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                    <div className="operations-toolbar-summary">
                        <span className="operations-pill operations-pill-date">{toDisplayDate(range.startDateKey)} - {toDisplayDate(range.endDateKey)}</span>
                        <span className="operations-pill operations-pill-accent">
                            {scopeKey === 'all'
                                ? 'All Owned Properties'
                                : `With ${(calendarData.partnerOptions || []).find((entry) => `partner:${entry.id}` === scopeKey)?.display || 'Partner'}`}
                        </span>
                    </div>
                    <div className="operations-toolbar-create-action">
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={() => {
                                setAppointmentFeedback({ type: '', text: '' });
                                setAppointmentForm((current) => ({
                                    ...current,
                                    appointmentDate: toDateKey(new Date())
                                }));
                                setIsCreateAppointmentModalOpen(true);
                            }}
                        >
                            Create Appointment
                        </button>
                    </div>
                </div>

                <div className="operations-property-chips">
                    {(calendarData.properties || []).map((property) => {
                        const selected = selectedPropertyIds.includes(Number(property.id));
                        return (
                            <button
                                key={property.id}
                                type="button"
                                className={`operations-property-chip${selected ? ' operations-property-chip-selected' : ''}`}
                                onClick={() => {
                                    setSelectedPropertyIds((current) => {
                                        const normalizedId = Number(property.id);
                                        if (current.includes(normalizedId)) {
                                            return current.filter((id) => id !== normalizedId);
                                        }
                                        return [...current, normalizedId];
                                    });
                                }}
                            >
                                {property.name}
                            </button>
                        );
                    })}
                    {selectedPropertyIds.length > 0 && (
                        <button type="button" className="operations-clear-chip" onClick={() => setSelectedPropertyIds([])}>
                            Clear Property Filters
                        </button>
                    )}
                </div>
            </section>

            {error && (
                <section className="property-pnl-card operations-error-card" role="alert">
                    <p>{error}</p>
                </section>
            )}

            <section className="property-pnl-card operations-timeline-card">
                <div className="operations-legend">
                    <div className="operations-legend-group operations-legend-group-left">
                        <span className="operations-legend-item"><span className="operations-legend-dot operations-legend-readiness-red" /> Needs Cleaners</span>
                        <span className="operations-legend-item"><span className="operations-legend-dot operations-legend-readiness-yellow" /> Needs supervizor</span>
                        <span className="operations-legend-item"><span className="operations-legend-dot operations-legend-readiness-neutral" /> Ready</span>
                    </div>
                    <div className="operations-legend-group operations-legend-group-center">
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => {
                                const base = parseDateOnlyUtc(anchorDay) || parseDateOnlyUtc(todayKey) || new Date();
                                setAnchorDay(toDateKey(addUtcDays(base, -7)));
                            }}
                        >
                            Back 7 Days
                        </button>
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => {
                                const base = parseDateOnlyUtc(anchorDay) || parseDateOnlyUtc(todayKey) || new Date();
                                setAnchorDay(toDateKey(addUtcDays(base, 7)));
                            }}
                        >
                            Forward 7 Days
                        </button>
                        <button type="button" className="btn-secondary" onClick={() => setAnchorDay(todayKey)}>Today</button>
                    </div>
                    <div className="operations-legend-group operations-legend-group-right">
                        <span className="operations-legend-item"><span className="operations-legend-icon operations-cell-indicator operations-cell-indicator-arrival">A</span> Arrival</span>
                        <span className="operations-legend-item"><span className="operations-legend-icon operations-cell-indicator operations-cell-indicator-departure">D</span> Departure</span>
                        <span className="operations-legend-item"><span className="operations-legend-icon operations-cell-indicator operations-cell-indicator-cleaning">CL</span> Cleaning</span>
                        <span className="operations-legend-item"><span className="operations-legend-icon operations-cell-indicator operations-cell-indicator-supervisor">S</span> Supervizor</span>
                    </div>
                </div>

                {loading ? (
                    <p className="operations-empty-copy">Loading operations calendar...</p>
                ) : filteredProperties.length === 0 ? (
                    <p className="operations-empty-copy">No owned properties found for this filter.</p>
                ) : (
                    <div className="operations-timeline-wrap">
                        <table className="operations-timeline-table">
                            <thead>
                                <tr>
                                    <th className="operations-sticky-col">Property</th>
                                    {dayKeys.map((dayKey) => {
                                        const dayDate = parseDateOnlyUtc(dayKey);
                                        const isToday = dayKey === todayKey;
                                        return (
                                            <th key={dayKey} className={`operations-day-head${isToday ? ' operations-day-head-today' : ''}`}>
                                                <span>{DAY_LABELS[dayDate?.getDay() || 0]}</span>
                                                <strong>{dayDate?.getDate() || ''}</strong>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredProperties.map((property) => {
                                    const propertyStats = dayStatsByProperty.get(Number(property.id));
                                    return (
                                        <tr key={property.id}>
                                            <th className="operations-sticky-col operations-property-cell">
                                                <div className="operations-property-name">{property.name}</div>
                                                <div className="operations-property-subtitle">{property.city}, {property.state}</div>
                                            </th>
                                            {dayKeys.map((dayKey) => {
                                                const stats = propertyStats?.get(dayKey) || { occupiedNights: 0, arrivals: 0, departures: 0 };
                                                const isOccupied = stats.occupiedNights > 0;
                                                const hasArrival = stats.arrivals > 0;
                                                const hasDeparture = stats.departures > 0;
                                                const hasTurnover = hasArrival && hasDeparture;
                                                const isActionable = hasArrival || hasDeparture;
                                                const cleaningCount = Array.isArray(stats.cleaningAppointments) ? stats.cleaningAppointments.length : 0;
                                                const supervisorCount = Array.isArray(stats.supervisorAppointments) ? stats.supervisorAppointments.length : 0;
                                                const hasAppointments = cleaningCount > 0 || supervisorCount > 0;
                                                const arrivalBookings = Array.isArray(stats.arrivalBookings) ? stats.arrivalBookings : [];
                                                const arrivalStatuses = arrivalBookings.map((booking) => getArrivalScheduleStatus(booking));
                                                const stayBookings = Array.isArray(stats.bookings) ? stats.bookings : [];
                                                const stayStatuses = stayBookings.map((booking) => getArrivalScheduleStatus(booking));
                                                const hasAttentionStatus = arrivalStatuses.some((status) => status === 'needs_cleaner');
                                                const hasCleanerOnlyStatus = arrivalStatuses.some((status) => status === 'cleaner_only');
                                                const hasAllScheduledStatus = arrivalStatuses.length > 0 && arrivalStatuses.every((status) => status === 'both');
                                                const hasStayAttentionStatus = stayStatuses.some((status) => status === 'needs_cleaner');
                                                const hasStayCleanerOnlyStatus = stayStatuses.some((status) => status === 'cleaner_only');
                                                const hasStayAllScheduledStatus = stayStatuses.length > 0 && stayStatuses.every((status) => status === 'both');
                                                const arrivalScheduleClass = hasArrival
                                                    ? hasAttentionStatus
                                                        ? 'operations-day-status-red'
                                                        : hasCleanerOnlyStatus
                                                            ? 'operations-day-status-yellow'
                                                            : hasAllScheduledStatus
                                                                ? 'operations-day-status-neutral'
                                                                : ''
                                                    : '';
                                                const stayScheduleClass = isOccupied
                                                    ? hasStayAttentionStatus
                                                        ? 'operations-day-status-red'
                                                        : hasStayCleanerOnlyStatus
                                                            ? 'operations-day-status-yellow'
                                                            : hasStayAllScheduledStatus
                                                                ? 'operations-day-status-neutral'
                                                                : ''
                                                    : '';
                                                const arrivalScheduleLabel = hasArrival
                                                    ? hasAttentionStatus
                                                        ? 'Arrival Prep: Missing cleaner and/or supervisor'
                                                        : hasCleanerOnlyStatus
                                                            ? 'Arrival Prep: Cleaner only'
                                                            : hasAllScheduledStatus
                                                                ? 'Arrival Prep: Cleaner and supervisor scheduled'
                                                                : 'Arrival Prep: Pending'
                                                    : 'Arrival Prep: N/A';
                                                const className = [
                                                    'operations-day-cell-button',
                                                    isOccupied ? 'operations-day-occupied' : '',
                                                    hasArrival ? 'operations-day-arrival' : '',
                                                    hasDeparture ? 'operations-day-departure' : '',
                                                    hasTurnover ? 'operations-day-turnover' : '',
                                                    isActionable ? 'operations-day-actionable' : '',
                                                    cleaningCount > 0 ? 'operations-day-has-cleaning' : '',
                                                    supervisorCount > 0 ? 'operations-day-has-supervisor' : '',
                                                    hasArrival
                                                        ? arrivalScheduleClass
                                                        : isOccupied
                                                            ? stayScheduleClass
                                                            : ''
                                                ].filter(Boolean).join(' ');

                                                return (
                                                    <td key={`${property.id}-${dayKey}`} className="operations-day-cell">
                                                        <button
                                                            type="button"
                                                            className={className}
                                                            title={`Arrivals: ${stats.arrivals} | Departures: ${stats.departures} | Cleaning: ${cleaningCount} | Supervisor: ${supervisorCount} | Occupied: ${stats.occupiedNights} | ${arrivalScheduleLabel} | Double-click or right-click to create an appointment`}
                                                            onClick={() => {
                                                                if (isActionable) {
                                                                    openBookingDetailModal(property, dayKey, stats);
                                                                } else if (hasAppointments) {
                                                                    openAppointmentDetailModal(property, dayKey, stats);
                                                                }
                                                            }}
                                                            onDoubleClick={() => openCreateAppointmentModalForCell(property, dayKey)}
                                                            onContextMenu={(event) => {
                                                                event.preventDefault();
                                                                openCreateAppointmentModalForCell(property, dayKey);
                                                            }}
                                                        >
                                                            {hasArrival && <span className="operations-cell-indicator operations-cell-indicator-arrival">A{stats.arrivals > 1 ? stats.arrivals : ''}</span>}
                                                            {hasDeparture && <span className="operations-cell-indicator operations-cell-indicator-departure">D{stats.departures > 1 ? stats.departures : ''}</span>}
                                                            {cleaningCount > 0 && <span className="operations-cell-indicator operations-cell-indicator-cleaning">CL{cleaningCount > 1 ? cleaningCount : ''}</span>}
                                                            {supervisorCount > 0 && <span className="operations-cell-indicator operations-cell-indicator-supervisor">S{supervisorCount > 1 ? supervisorCount : ''}</span>}
                                                        </button>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                                <tr className="operations-metric-row operations-metric-row-arrivals">
                                    <th className="operations-sticky-col operations-metric-label">Portfolio Check-ins</th>
                                    {dayKeys.map((dayKey) => {
                                        const total = Number(gridDailyTotals.get(dayKey)?.arrivals || 0);
                                        return (
                                            <td key={`arrivals-${dayKey}`} className="operations-metric-cell">
                                                {total > 0 ? total : '0'}
                                            </td>
                                        );
                                    })}
                                </tr>
                                <tr className="operations-metric-row operations-metric-row-departures">
                                    <th className="operations-sticky-col operations-metric-label">Portfolio Check-outs</th>
                                    {dayKeys.map((dayKey) => {
                                        const total = Number(gridDailyTotals.get(dayKey)?.departures || 0);
                                        return (
                                            <td key={`departures-${dayKey}`} className="operations-metric-cell">
                                                {total > 0 ? total : '0'}
                                            </td>
                                        );
                                    })}
                                </tr>
                                <tr className="operations-metric-row operations-metric-row-turnovers">
                                    <th className="operations-sticky-col operations-metric-label">Portfolio Turnovers</th>
                                    {dayKeys.map((dayKey) => {
                                        const total = Number(gridDailyTotals.get(dayKey)?.turnovers || 0);
                                        return (
                                            <td key={`turnovers-${dayKey}`} className="operations-metric-cell">
                                                {total > 0 ? total : '0'}
                                            </td>
                                        );
                                    })}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section className="operations-summary-row">
                <div className="property-pnl-card operations-summary-card">
                    <span>Booked Nights</span>
                    <strong>{portfolioStats.occupiedNights}</strong>
                </div>
                <div className="property-pnl-card operations-summary-card">
                    <span>Arrivals</span>
                    <strong>{portfolioStats.arrivals}</strong>
                </div>
                <div className="property-pnl-card operations-summary-card">
                    <span>Departures</span>
                    <strong>{portfolioStats.departures}</strong>
                </div>
                <div className="property-pnl-card operations-summary-card">
                    <span>Turnovers</span>
                    <strong>{portfolioStats.turnovers}</strong>
                </div>
            </section>

            {isCreateAppointmentModalOpen && (
                <div className="modal-overlay" onClick={closeCreateAppointmentModal}>
                    <div className="modal-content operations-create-appointment-modal" onClick={(event) => event.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Create Appointment</h3>
                            <button className="modal-close" onClick={closeCreateAppointmentModal}>&times;</button>
                        </div>
                        <div className="operations-booking-details-body">
                            {appointmentFeedback.text && (
                                <div className={`operations-appointment-feedback operations-appointment-feedback-${appointmentFeedback.type === 'success' ? 'success' : 'error'}`}>
                                    {appointmentFeedback.text}
                                </div>
                            )}
                            <form className="operations-appointment-form" onSubmit={createAppointment}>
                                <label className="operations-filter-field">
                                    <span>Appointment Type</span>
                                    <select value={appointmentForm.type} onChange={(event) => setAppointmentForm((current) => ({ ...current, type: event.target.value }))}>
                                        <option value="cleaning">Cleaning</option>
                                        <option value="supervisor">Supervisor</option>
                                    </select>
                                </label>
                                <label className="operations-filter-field">
                                    <span>Property</span>
                                    <select value={appointmentForm.propertyId} onChange={(event) => setAppointmentForm((current) => ({ ...current, propertyId: event.target.value }))}>
                                        <option value="">Select property</option>
                                        {(calendarData.properties || []).map((property) => (
                                            <option key={`appointment-${property.id}`} value={property.id}>{property.name}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="operations-filter-field">
                                    <span>Appointment Date</span>
                                    <input type="date" value={appointmentForm.appointmentDate} onChange={(event) => setAppointmentForm((current) => ({ ...current, appointmentDate: event.target.value }))} />
                                </label>
                                {appointmentForm.type === 'cleaning' ? (
                                    <>
                                        <label className="operations-filter-field">
                                            <span>Cleaner</span>
                                            <select required value={appointmentForm.cleanerId} onChange={(event) => setAppointmentForm((current) => ({ ...current, cleanerId: event.target.value, cleanerName: '' }))}>
                                                <option value="">Select a shared cleaner</option>
                                                {calendarData.cleanerOptions.map((cleaner) => (
                                                    <option key={cleaner.cleanerId} value={cleaner.cleanerId}>{cleaner.display}</option>
                                                ))}
                                            </select>
                                        </label>
                                        <label className="operations-filter-field">
                                            <span>Cleaning Cost</span>
                                            <input type="number" min="0" step="0.01" value={appointmentForm.cost} onChange={(event) => setAppointmentForm((current) => ({ ...current, cost: event.target.value }))} placeholder="0.00" />
                                        </label>
                                    </>
                                ) : (
                                    <label className="operations-filter-field">
                                        <span>Supervisor Name</span>
                                        <input type="text" value={appointmentForm.supervisorName} onChange={(event) => setAppointmentForm((current) => ({ ...current, supervisorName: event.target.value }))} placeholder="Supervisor name" />
                                    </label>
                                )}
                                <div className="operations-appointment-actions">
                                    <button type="submit" className="btn-primary" disabled={creatingAppointment}>
                                        {creatingAppointment ? 'Saving...' : 'Create Appointment'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {bookingDetailModal.isOpen && (
                <div className="modal-overlay" onClick={closeBookingDetailModal}>
                    <div className="modal-content operations-booking-details-modal" onClick={(event) => event.stopPropagation()}>
                        <div className="modal-header">
                            <h3>
                                {bookingDetailModal.mode === 'turnover'
                                    ? 'Turnover Details'
                                    : bookingDetailModal.mode === 'arrival'
                                        ? 'Arrival Details'
                                        : 'Departure Details'}: {bookingDetailModal.property?.name || 'Property'} ({toDisplayDate(bookingDetailModal.dayKey)})
                            </h3>
                            <button className="modal-close" onClick={closeBookingDetailModal}>&times;</button>
                        </div>
                        <div className="operations-booking-details-body">
                            {bookingDetailError && <p className="chat-error">{bookingDetailError}</p>}
                            {bookingDetailModal.entries.length === 0 ? (
                                <p className="operations-empty-copy">No arrival or departure bookings for this date.</p>
                            ) : (
                                <div className="operations-booking-details-layout">
                                    <section className="operations-booking-details-panel">
                                        <div className="operations-booking-card-list">
                                                {bookingDetailModal.entries.map((entry) => {
                                                    const booking = entry.booking || {};
                                                    const arrivalDayKey = String(booking.startDate || '').slice(0, 10);
                                                    const propertyId = Number(booking.propertyId);
                                                    const cleanerScheduled = entry.isArrival
                                                        ? hasAssignedCleanerForBooking(booking.id)
                                                        : null;
                                                    const supervisorScheduled = entry.isArrival
                                                        ? isRoleScheduledForArrival(supervisorAppointmentDayKeysByProperty, propertyId, arrivalDayKey)
                                                        : null;
                                                    const prepStatus = entry.isArrival
                                                        ? !cleanerScheduled
                                                            ? 'needs_cleaner'
                                                            : !supervisorScheduled
                                                                ? 'cleaner_only'
                                                                : 'both'
                                                        : 'none';
                                                    const prepStatusClass = prepStatus === 'needs_cleaner'
                                                        ? 'operations-booking-row-status-red'
                                                        : prepStatus === 'cleaner_only'
                                                            ? 'operations-booking-row-status-yellow'
                                                            : prepStatus === 'both'
                                                                ? 'operations-booking-row-status-neutral'
                                                                : '';
                                                    const bookingId = Number(booking.id);
                                                    const cleaningJob = (calendarData.cleaningJobs || []).find((job) => Number(job.bookingId) === bookingId);
                                                    const selectedCleanerId = cleanerByJobId[cleaningJob?.id] || '';
                                                    const draft = bookingTimeDrafts[bookingId] || {
                                                        checkInTime: normalizeTimeValue(booking.checkInTime),
                                                        checkOutTime: normalizeTimeValue(booking.checkOutTime)
                                                    };
                                                    const isSaving = savingBookingIds.includes(bookingId);

                                                    return (
                                                        <article key={`booking-detail-${bookingId}`} className={`operations-booking-card ${prepStatusClass}`}>
                                                            <div className="operations-booking-card-title-row">
                                                                <div className="operations-booking-id">#{bookingId}</div>
                                                                <div className="operations-booking-description">{booking.description || booking.source || 'Booking'}</div>
                                                                <div>
                                                                    {entry.isArrival && <span className="operations-booking-type-chip operations-booking-type-arrival">Arrival</span>}
                                                                    {entry.isDeparture && <span className="operations-booking-type-chip operations-booking-type-departure">Departure</span>}
                                                                </div>
                                                                {entry.isArrival && (
                                                                    <div className="operations-booking-prep-chip-wrap">
                                                                        <span className={`operations-booking-prep-chip ${prepStatus === 'needs_cleaner' ? 'operations-booking-prep-chip-red' : prepStatus === 'cleaner_only' ? 'operations-booking-prep-chip-yellow' : 'operations-booking-prep-chip-neutral'}`}>
                                                                            {prepStatus === 'needs_cleaner' ? 'Needs Cleaner' : prepStatus === 'cleaner_only' ? 'Needs Supervisor' : 'Ready'}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                <div className="operations-booking-amount">{formatCurrency(booking.amount)}</div>
                                                            </div>
                                                            <div className="operations-booking-card-grid">
                                                                <label className="operations-card-field">
                                                                    <span>Check-in Date</span>
                                                                    <strong>{toDisplayDate(booking.startDate)}</strong>
                                                                </label>
                                                                <label className="operations-card-field">
                                                                    <span>Check-out Date</span>
                                                                    <strong>{toDisplayDate(booking.endDate)}</strong>
                                                                </label>
                                                                <label className="operations-card-field">
                                                                    <span>Check-in Time</span>
                                                                    <input
                                                                        type="time"
                                                                        value={draft.checkInTime}
                                                                        onChange={(event) => {
                                                                            const value = normalizeTimeValue(event.target.value);
                                                                            setBookingTimeDrafts((current) => ({
                                                                                ...current,
                                                                                [bookingId]: {
                                                                                    ...(current[bookingId] || {}),
                                                                                    checkInTime: value
                                                                                }
                                                                            }));
                                                                        }}
                                                                    />
                                                                </label>
                                                                <label className="operations-card-field">
                                                                    <span>Check-out Time</span>
                                                                    <input
                                                                        type="time"
                                                                        value={draft.checkOutTime}
                                                                        onChange={(event) => {
                                                                            const value = normalizeTimeValue(event.target.value);
                                                                            setBookingTimeDrafts((current) => ({
                                                                                ...current,
                                                                                [bookingId]: {
                                                                                    ...(current[bookingId] || {}),
                                                                                    checkOutTime: value
                                                                                }
                                                                            }));
                                                                        }}
                                                                    />
                                                                </label>
                                                                <label className="operations-card-field">
                                                                    <span>Cleaner Scheduled</span>
                                                                    {entry.isArrival ? (
                                                                        <span className={`operations-cleaner-indicator ${prepStatus === 'needs_cleaner' ? 'operations-cleaner-indicator-false' : prepStatus === 'cleaner_only' ? 'operations-cleaner-indicator-warn' : 'operations-cleaner-indicator-true'}`}>
                                                                            {cleanerScheduled ? 'True' : 'False'}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="operations-cleaner-indicator operations-cleaner-indicator-na">-</span>
                                                                    )}
                                                                </label>
                                                                <label className="operations-card-field">
                                                                    <span>Supervisor Scheduled</span>
                                                                    {entry.isArrival ? (
                                                                        <span className={`operations-cleaner-indicator ${prepStatus === 'needs_cleaner' ? 'operations-cleaner-indicator-false' : prepStatus === 'cleaner_only' ? 'operations-cleaner-indicator-warn' : 'operations-cleaner-indicator-true'}`}>
                                                                            {supervisorScheduled ? 'True' : 'False'}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="operations-cleaner-indicator operations-cleaner-indicator-na">-</span>
                                                                    )}
                                                                </label>
                                                                <label className="operations-card-field operations-booking-cleaner-assignment">
                                                                    <span>Booking Cleaner</span>
                                                                    {cleaningJob ? (
                                                                        cleaningJob.status === 'open' ? (
                                                                            <select
                                                                                value={selectedCleanerId}
                                                                                onChange={(event) => setCleanerByJobId((current) => ({ ...current, [cleaningJob.id]: event.target.value }))}
                                                                            >
                                                                                <option value="">{cleaningJob.offeredCleanerName ? `Offer pending: ${cleaningJob.offeredCleanerName}` : 'Select a cleaner'}</option>
                                                                                {calendarData.cleanerOptions.map((cleaner) => (
                                                                                    <option key={cleaner.cleanerId} value={cleaner.cleanerId}>{cleaner.display}</option>
                                                                                ))}
                                                                            </select>
                                                                        ) : (
                                                                            <strong>{cleaningJob.assignedCleanerName || cleaningJob.status}</strong>
                                                                        )
                                                                    ) : <strong>No linked cleaning job</strong>}
                                                                </label>
                                                            </div>
                                                            <div className="operations-booking-card-actions">
                                                                <button
                                                                    type="button"
                                                                    className="btn-secondary btn-inline"
                                                                    disabled={isSaving}
                                                                    onClick={() => saveBookingTimes(entry)}
                                                                >
                                                                    {isSaving ? 'Saving...' : 'Save Times'}
                                                                </button>
                                                                {cleaningJob?.status === 'open' && selectedCleanerId && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn-primary btn-inline"
                                                                        disabled={isSaving}
                                                                        onClick={() => assignBookingCleaner(cleaningJob)}
                                                                    >
                                                                        Offer Cleaner
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </article>
                                                    );
                                                })}
                                    </div>
                                    </section>
                                    {(bookingDetailModal.cleaningAppointments.length > 0 || bookingDetailModal.supervisorAppointments.length > 0) && (
                                        <aside className="operations-embedded-appointments operations-booking-details-panel">
                                            <div className="operations-embedded-appointments-header">
                                                <h4>Same-Day Appointments</h4>
                                                <button
                                                    type="button"
                                                    className="btn-secondary btn-inline"
                                                    onClick={() => {
                                                        const property = bookingDetailModal.property;
                                                        const dayKey = bookingDetailModal.dayKey;
                                                        const stats = {
                                                            cleaningAppointments: bookingDetailModal.cleaningAppointments,
                                                            supervisorAppointments: bookingDetailModal.supervisorAppointments
                                                        };
                                                        closeBookingDetailModal();
                                                        openAppointmentDetailModal(property, dayKey, stats);
                                                    }}
                                                >
                                                    Edit Appointments
                                                </button>
                                            </div>
                                            <div className="operations-appointment-card-list">
                                                        {bookingDetailModal.cleaningAppointments.map((appointment) => {
                                                            const hasSameDayCleaning = bookingDetailModal.cleaningAppointments.length > 0;
                                                            const hasSameDaySupervisor = bookingDetailModal.supervisorAppointments.length > 0;
                                                            const appointmentStatusClass = hasSameDayCleaning && hasSameDaySupervisor
                                                                ? 'operations-booking-row-status-neutral'
                                                                : hasSameDayCleaning
                                                                    ? 'operations-booking-row-status-yellow'
                                                                    : 'operations-booking-row-status-red';
                                                            return (
                                                            <article key={`embedded-cleaning-${appointment.id}`} className={`operations-appointment-card ${appointmentStatusClass} operations-appointment-row-cleaning`}>
                                                                <div><span className="operations-booking-type-chip operations-appointment-type-cleaning">Cleaning</span></div>
                                                                <div className="operations-appointment-card-grid">
                                                                    <label className="operations-card-field"><span>Cleaner</span><strong>{appointment.cleanerName || '-'}</strong></label>
                                                                    <label className="operations-card-field"><span>Date</span><strong>{toDisplayDate(appointment.appointmentDate)}</strong></label>
                                                                    <label className="operations-card-field"><span>Cost</span><strong>{formatCurrency(appointment.cost)}</strong></label>
                                                                </div>
                                                            </article>
                                                        );})}
                                                        {bookingDetailModal.supervisorAppointments.map((appointment) => {
                                                            const hasSameDayCleaning = bookingDetailModal.cleaningAppointments.length > 0;
                                                            const hasSameDaySupervisor = bookingDetailModal.supervisorAppointments.length > 0;
                                                            const appointmentStatusClass = hasSameDayCleaning && hasSameDaySupervisor
                                                                ? 'operations-booking-row-status-neutral'
                                                                : hasSameDayCleaning
                                                                    ? 'operations-booking-row-status-yellow'
                                                                    : 'operations-booking-row-status-red';
                                                            return (
                                                            <article key={`embedded-supervisor-${appointment.id}`} className={`operations-appointment-card ${appointmentStatusClass} operations-appointment-row-supervisor`}>
                                                                <div><span className="operations-booking-type-chip operations-appointment-type-supervisor">Supervisor</span></div>
                                                                <div className="operations-appointment-card-grid">
                                                                    <label className="operations-card-field"><span>Supervisor</span><strong>{appointment.supervisorName || '-'}</strong></label>
                                                                    <label className="operations-card-field"><span>Date</span><strong>{toDisplayDate(appointment.appointmentDate)}</strong></label>
                                                                    <label className="operations-card-field"><span>Cost</span><strong>-</strong></label>
                                                                </div>
                                                            </article>
                                                        );})}
                                            </div>
                                        </aside>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {appointmentDetailModal.isOpen && (
                <div className="modal-overlay" onClick={closeAppointmentDetailModal}>
                    <div className="modal-content operations-booking-details-modal" onClick={(event) => event.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Appointment Details: {appointmentDetailModal.property?.name || 'Property'} ({toDisplayDate(appointmentDetailModal.dayKey)})</h3>
                            <button className="modal-close" onClick={closeAppointmentDetailModal}>&times;</button>
                        </div>
                        <div className="operations-booking-details-body">
                            {appointmentDetailError && <p className="chat-error">{appointmentDetailError}</p>}
                            {appointmentDetailModal.entries.length === 0 ? (
                                <p className="operations-empty-copy">No appointments for this date.</p>
                            ) : (
                                <div className="operations-appointment-card-list operations-appointment-card-list-editable">
                                            {appointmentDetailModal.entries.map((entry) => {
                                                const hasDayCleaning = appointmentDetailModal.entries.some((item) => item.type === 'cleaning');
                                                const hasDaySupervisor = appointmentDetailModal.entries.some((item) => item.type === 'supervisor');
                                                const appointmentStatusClass = hasDayCleaning && hasDaySupervisor
                                                    ? 'operations-booking-row-status-neutral'
                                                    : hasDayCleaning
                                                        ? 'operations-booking-row-status-yellow'
                                                        : 'operations-booking-row-status-red';
                                                const draft = appointmentDrafts[entry.id] || {
                                                    appointmentDate: String(entry.appointment?.appointmentDate || '').slice(0, 10),
                                                    cleanerId: entry.type === 'cleaning' && entry.appointment?.cleanerId ? String(entry.appointment.cleanerId) : '',
                                                    cleanerName: entry.type === 'cleaning' ? String(entry.personName || '') : '',
                                                    supervisorName: entry.type === 'supervisor' ? String(entry.personName || '') : '',
                                                    cost: entry.type === 'cleaning' ? String(entry.cost ?? '') : ''
                                                };
                                                const isSaving = savingAppointmentEntryIds.includes(String(entry.id));
                                                const isDeleting = deletingAppointmentEntryIds.includes(String(entry.id));
                                                return (
                                                    <article key={entry.id} className={`operations-appointment-card ${appointmentStatusClass} ${entry.type === 'cleaning' ? 'operations-appointment-row-cleaning' : 'operations-appointment-row-supervisor'}`}>
                                                        <div>
                                                            <span className={`operations-booking-type-chip ${entry.type === 'cleaning' ? 'operations-appointment-type-cleaning' : 'operations-appointment-type-supervisor'}`}>
                                                                {entry.type === 'cleaning' ? 'Cleaning' : 'Supervisor'}
                                                            </span>
                                                        </div>
                                                        <div className="operations-appointment-card-grid">
                                                            <label className="operations-card-field">
                                                                <span>{entry.type === 'cleaning' ? 'Cleaner' : 'Supervisor'}</span>
                                                                {entry.type === 'cleaning' ? (
                                                                                        <select
                                                                                            className="operations-appointment-edit-input"
                                                                                            value={draft.cleanerId || ''}
                                                                                            onChange={(event) => {
                                                                                                const cleanerId = event.target.value;
                                                                                                const cleanerName = calendarData.cleanerOptions.find((cleaner) => String(cleaner.cleanerId) === cleanerId)?.display || '';
                                                                                                setAppointmentDrafts((current) => ({
                                                                                                    ...current,
                                                                                                    [entry.id]: { ...(current[entry.id] || {}), cleanerId, cleanerName }
                                                                                                }));
                                                                                            }}
                                                                                        >
                                                                                            {draft.cleanerName && !draft.cleanerId && <option value="">Legacy: {draft.cleanerName}</option>}
                                                                                            {calendarData.cleanerOptions.map((cleaner) => <option key={cleaner.cleanerId} value={cleaner.cleanerId}>{cleaner.display}</option>)}
                                                                                        </select>
                                                                ) : (
                                                                <input
                                                                    className="operations-appointment-edit-input"
                                                                    type="text"
                                                                    value={draft.supervisorName}
                                                                    onChange={(event) => {
                                                                        const value = event.target.value;
                                                                        setAppointmentDrafts((current) => ({
                                                                            ...current,
                                                                            [entry.id]: {
                                                                                ...(current[entry.id] || {}),
                                                                                supervisorName: value
                                                                            }
                                                                        }));
                                                                    }}
                                                                />
                                                                )}
                                                            </label>
                                                            <label className="operations-card-field">
                                                                <span>Date</span>
                                                                <input
                                                                className="operations-appointment-edit-input"
                                                                type="date"
                                                                value={draft.appointmentDate}
                                                                onChange={(event) => {
                                                                    const value = String(event.target.value || '').slice(0, 10);
                                                                    setAppointmentDrafts((current) => ({
                                                                        ...current,
                                                                        [entry.id]: {
                                                                            ...(current[entry.id] || {}),
                                                                            appointmentDate: value
                                                                        }
                                                                    }));
                                                                }}
                                                            />
                                                            </label>
                                                            <label className="operations-card-field">
                                                                <span>Cost</span>
                                                            {entry.type === 'cleaning' ? (
                                                                <input
                                                                    className="operations-appointment-edit-input"
                                                                    type="number"
                                                                    min="0"
                                                                    step="0.01"
                                                                    value={draft.cost}
                                                                    onChange={(event) => {
                                                                        const value = event.target.value;
                                                                        setAppointmentDrafts((current) => ({
                                                                            ...current,
                                                                            [entry.id]: {
                                                                                ...(current[entry.id] || {}),
                                                                                cost: value
                                                                            }
                                                                        }));
                                                                    }}
                                                                />
                                                            ) : '-'}
                                                            </label>
                                                        </div>
                                                        <div className="operations-appointment-card-actions">
                                                            <button
                                                                type="button"
                                                                className="btn-secondary btn-inline"
                                                                disabled={isSaving || isDeleting}
                                                                onClick={() => saveAppointment(entry)}
                                                            >
                                                                {isSaving ? 'Saving...' : 'Save'}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn-secondary btn-inline operations-delete-btn"
                                                                disabled={isSaving || isDeleting}
                                                                onClick={() => deleteAppointment(entry)}
                                                            >
                                                                {isDeleting ? 'Deleting...' : 'Delete'}
                                                            </button>
                                                        </div>
                                                    </article>
                                                );
                                            })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {createAppointmentToast && (
                <div className="operations-toast operations-toast-success" role="status" aria-live="polite">
                    {createAppointmentToast}
                </div>
            )}

        </div>
    );
};

export default OperationsPage;
